import React, { useState, createContext, useContext } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import CenterLogo from './components/CenterLogo';
import MobileContactButtons from './components/MobileContactButtons';
import SiteFooter from './components/SiteFooter';
import DemoGate from './components/DemoGate';
import Login from './pages/Login';
import Listings from './pages/Listings';
import CarDetails from './pages/CarDetails';
import Dashboard from './pages/Dashboard';
import AddCar from './pages/AddCar';
import EditCar from './pages/EditCar';
import BuyBack from './pages/BuyBack';
import BuyBackAdmin from './pages/BuyBackAdmin';
import GenerateContract from './pages/GenerateContract';
import SiteSettings from './pages/SiteSettings';
import { ThemeProvider, useTheme } from './theme';

const LanguageContext = createContext();
const AuthContext = createContext({ token: '', setToken: () => {} });
const translations = {
  en: {
    listings: 'Listings',
    addCar: 'Add Car',
    dashboard: 'Dashboard',
    login: 'Admin Access',
    language: 'Language',
    welcome: 'Welcome to the Car Marketplace!',
    adminPanel: 'Admin Panel',
    requestEvaluation: 'Get an Appraisal',
    modifyListing: 'Modify Listing',
    buyBack: 'Trade-In',
    generateContract: 'Generate Contract',
    siteSettings: 'Site Settings',
    logout: 'Log out',
  },
  ro: {
  listings: 'Mașinile Disponibile',
    addCar: 'Adaugă Mașină',
    dashboard: 'Panou Utilizator',
      login: 'Acces Administrator',
      language: 'Limba',
      welcome: 'Bine ați venit la Piața Auto!',
      adminPanel: 'Modifică anunțurile',
    requestEvaluation: 'Solicită evaluare',
    modifyListing: 'Modifică anunț',
    buyBack: 'Buy-back',
    generateContract: 'Generează contract',
    siteSettings: 'Setări site',
    logout: 'Ieși de pe cont',
  }
};

function useLanguage() {
  return useContext(LanguageContext);
}

function useAuth() {
  return useContext(AuthContext);
}

function LanguageSwitcher() {
  const { language, setLanguage } = useLanguage();
  const { tokens } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke={tokens.text} strokeWidth="2" fill={tokens.panel} />
        <text x="12" y="16" textAnchor="middle" fontSize="12" fill={tokens.text}>🌐</text>
      </svg>
      <select
        value={language}
        onChange={e => setLanguage(e.target.value)}
        style={{
          fontSize: 16,
          background: tokens.panel,
          color: tokens.text,
          border: `1px solid ${tokens.mutedBorder}`,
          borderRadius: 4
        }}
      >
        <option value="en">EN</option>
        <option value="ro">RO</option>
      </select>
    </div>
  );
}

function Navbar() {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false); // for animation transitions
  const navRef = React.useRef(null);
  const { language } = useLanguage();
  const t = translations[language];
  const { token, setToken } = useAuth();
  const { tokens } = useTheme();
  const isAuth = !!token;
  const navigate = require('react-router-dom').useNavigate();
  function logout() {
    try { localStorage.removeItem('token'); } catch {}
    setToken('');
    // soft navigate home so routes update without full reload
    navigate('/');
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 700;
  const desktopScale = !isMobile ? 0.9 : 1; // scale down menus by 10% on desktop
  // CONTROL: desktop hover/clickable width for each top-bar tab (horizontal padding in px)
  const desktopHoverPadX = 18; // keep clickable area original; hover visual will extend via CSS
  const itemPadX = !isMobile ? desktopHoverPadX : 0;
  // Make hover/click area match the full visual bar height despite scaling
  const navPaddingY = 20; // must match <nav> padding top/bottom
  const verticalPad = !isMobile ? Math.round(navPaddingY / desktopScale) : 0;

  const openMenu = () => {
    setShowMobileMenu(true);
    // Wait a tick so CSS transitions can run on mount
    requestAnimationFrame(() => setMenuVisible(true));
    try { document.body.style.overflow = 'hidden'; } catch {}
  };

  const closeMenu = () => {
    setMenuVisible(false);
    // Unmount after animation
    setTimeout(() => setShowMobileMenu(false), 280);
    try { document.body.style.overflow = ''; } catch {}
  };

  // Build mobile menu items (with login icon when not authenticated)
  const menuItems = [
    { to: '/', label: t.listings },
    ...(!isAuth ? [{ to: '/evaluare', label: t.requestEvaluation }] : []),
    ...(isAuth ? [{ to: '/add', label: t.addCar }] : []),
    ...(isAuth ? [{ to: '/dashboard', label: t.modifyListing }] : []),
    ...(isAuth ? [{ to: '/admin/buyback', label: t.buyBack }] : []),
    ...(isAuth ? [{ to: '/admin/generate-contract', label: t.generateContract }] : []),
    ...(isAuth ? [{ to: '/settings', label: t.siteSettings }] : []),
    ...(!isAuth ? [{ to: '/login', label: t.login }] : []),
    ...(isAuth ? [{ action: 'logout', label: t.logout }] : []),
  ];

  // Close on ESC and clean up
  React.useEffect(() => {
    if (!showMobileMenu) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showMobileMenu]);

  return (
    <nav
      ref={navRef}
      style={{
        display: isMobile ? 'grid' : 'flex',
        gridTemplateColumns: isMobile ? '48px 1fr auto' : undefined,
        alignItems: 'center',
        justifyContent: isMobile ? undefined : 'center',
        padding: 20,
        background: tokens.surface,
        marginBottom: 32,
        position: 'relative',
        boxShadow: `0 2px 8px ${tokens.shadowSoft}`,
      }}
    >
      {/* Hamburger for mobile */}
      <button
        style={{
          display: isMobile ? 'block' : 'none',
          position: isMobile ? 'relative' : 'absolute',
          left: isMobile ? undefined : 18,
          top: isMobile ? undefined : 18,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          zIndex: 100,
        }}
        onClick={() => (showMobileMenu ? closeMenu() : openMenu())}
        aria-label="Open menu"
      >
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <rect y="7" width="32" height="4" rx="2" fill={tokens.accent} />
          <rect y="15" width="32" height="4" rx="2" fill={tokens.accent} />
          <rect y="23" width="32" height="4" rx="2" fill={tokens.accent} />
        </svg>
      </button>
      {/* Mobile menu overlay */}
      {showMobileMenu && (
        <div
          onClick={(e) => { if (e.currentTarget === e.target) closeMenu(); }}
          style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          background: tokens.overlayBg,
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          zIndex: 99,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: menuVisible ? 1 : 0,
          transition: 'opacity 280ms ease',
        }}
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={closeMenu}
            style={{
              position: 'absolute',
              top: 18,
              right: 18,
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: 32,
              cursor: 'pointer'
            }}
            aria-label="Close menu"
          >
            ×
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: tokens.overlaySurface,
              border: `1px solid ${tokens.border}`,
              borderRadius: 16,
              boxShadow: `0 24px 60px ${tokens.shadow}`,
              padding: '28px 24px',
              width: 'min(90vw, 440px)',
              transform: menuVisible ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.98)',
              opacity: menuVisible ? 1 : 0,
              transition: 'transform 300ms cubic-bezier(.2,.8,.2,1), opacity 300ms ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {menuItems.map((item, idx) => (
              <React.Fragment key={item.to || item.action}>
                {item.to ? (
                  <Link
                    to={item.to}
                    onClick={closeMenu}
                    style={{
                      color: tokens.text,
                      fontWeight: 800,
                      fontSize: 24,
                      margin: '10px 0 6px',
                      textDecoration: 'none',
                      letterSpacing: 0.2,
                      opacity: menuVisible ? 1 : 0,
                      transform: menuVisible ? 'translateY(0)' : 'translateY(8px)',
                      transition: 'transform 320ms cubic-bezier(.2,.8,.2,1), opacity 320ms ease',
                      transitionDelay: `${100 + idx * 60}ms`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    {/* only show user icon next to login */}
                    {item.to === '/login' && (
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="12" fill={tokens.accent} />
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill={tokens.inverse} />
                      </svg>
                    )}
                    {item.label}
                  </Link>
                ) : (
                  <button
                    onClick={() => { closeMenu(); logout(); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: tokens.text,
                      fontWeight: 800,
                      fontSize: 24,
                      margin: '10px 0 6px',
                      letterSpacing: 0.2,
                      opacity: menuVisible ? 1 : 0,
                      transform: menuVisible ? 'translateY(0)' : 'translateY(8px)',
                      transition: 'transform 320ms cubic-bezier(.2,.8,.2,1), opacity 320ms ease',
                      transitionDelay: `${100 + idx * 60}ms`,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    {/* show same icon for logout for visual consistency */}
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="12" fill={tokens.accent} />
                      <path d="M10 7h2v4h4v2h-4v4h-2V7zm-2 2v6H6V9h2z" fill={tokens.inverse} />
                    </svg>
                    {item.label}
                  </button>
                )}
                {idx < menuItems.length - 1 && (
                  <div
                    style={{
                      height: 2,
                      width: '75%',
                      background: tokens.accent,
                      borderRadius: 2,
                      opacity: menuVisible ? 0.85 : 0,
                      transition: 'opacity 300ms ease',
                      transitionDelay: `${140 + idx * 60}ms`
                    }}
                  />
                )}
              </React.Fragment>
            ))}

            {/* Language switcher inline at bottom for mobile menu */}
            <div style={{ marginTop: 16, opacity: menuVisible ? 1 : 0, transition: 'opacity 300ms ease', display: 'flex', alignItems: 'center', gap: 12 }}>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
      {/* LEFT LINKS (scaled down 10% on desktop) */}
      <div style={{
        flex: 1,
        display: window.innerWidth < 700 ? 'none' : 'flex',
        alignItems: 'center',
        gap: 18,
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
        transform: isMobile ? undefined : `scale(${desktopScale})`,
        transformOrigin: 'left center'
      }}>
        <div style={{ display: window.innerWidth < 700 ? 'none' : 'flex', alignItems: 'center', gap: 18, position: 'relative', height: 38, flexWrap: 'nowrap' }}>
          <div className="nav-item" style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '200%', padding: `0 ${itemPadX}px` }}>
            <Link to="/" className="nav-link" style={{ color: tokens.text, textDecoration: 'none', fontWeight: 600, fontSize: 18, position: 'relative', display: 'flex', alignItems: 'center', height: '100%', width: '100%', paddingTop: verticalPad, paddingBottom: verticalPad, marginTop: -verticalPad, marginBottom: -verticalPad }}>
              {t.listings}
            </Link>
          </div>
          {/* evaluation link for guests */}
          {!isAuth && (
            <>
              <div aria-hidden style={{ width: 1, height: 20, background: 'linear-gradient(to bottom, rgba(0,0,0,0), #2b2b2b 30%, #2b2b2b 70%, rgba(0,0,0,0))' }} />
              <div className="nav-item" style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '200%', padding: `0 ${itemPadX}px` }}>
                <Link to="/evaluare" className="nav-link" style={{ color: tokens.text, textDecoration: 'none', fontWeight: 600, fontSize: 18, position: 'relative', display: 'flex', alignItems: 'center', height: '100%', width: '100%', paddingTop: verticalPad, paddingBottom: verticalPad, marginTop: -verticalPad, marginBottom: -verticalPad }}>
                  {t.requestEvaluation}
                </Link>
              </div>
            </>
          )}
          {/* fine vertical separator */}
          {isAuth && (
            <div aria-hidden style={{ width: 1, height: 20, background: 'linear-gradient(to bottom, rgba(0,0,0,0), #2b2b2b 30%, #2b2b2b 70%, rgba(0,0,0,0))' }} />
          )}
          {isAuth && (
            <>
              <div className="nav-item" style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '200%', padding: `0 ${itemPadX}px` }}>
                <Link to="/add" className="nav-link" style={{ color: tokens.text, textDecoration: 'none', fontWeight: 600, fontSize: 18, position: 'relative', display: 'flex', alignItems: 'center', height: '100%', width: '100%', paddingTop: verticalPad, paddingBottom: verticalPad, marginTop: -verticalPad, marginBottom: -verticalPad }}>
                  {t.addCar}
                </Link>
              </div>
              {/* fine vertical separator */}
              <div aria-hidden style={{ width: 1, height: 20, background: 'linear-gradient(to bottom, rgba(0,0,0,0), #2b2b2b 30%, #2b2b2b 70%, rgba(0,0,0,0))' }} />
              <div className="nav-item" style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '200%', padding: `0 ${itemPadX}px` }}>
                <Link to="/dashboard" className="nav-link" style={{ color: tokens.text, textDecoration: 'none', fontWeight: 600, fontSize: 18, position: 'relative', display: 'flex', alignItems: 'center', height: '100%', width: '100%', paddingTop: verticalPad, paddingBottom: verticalPad, marginTop: -verticalPad, marginBottom: -verticalPad }}>
                  {t.modifyListing}
                </Link>
              </div>
              {/* fine vertical separator */}
              <div aria-hidden style={{ width: 1, height: 20, background: 'linear-gradient(to bottom, rgba(0,0,0,0), #2b2b2b 30%, #2b2b2b 70%, rgba(0,0,0,0))' }} />
              <div className="nav-item" style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '200%', padding: `0 ${itemPadX}px` }}>
                <Link to="/admin/buyback" className="nav-link" style={{ color: tokens.text, textDecoration: 'none', fontWeight: 600, fontSize: 18, position: 'relative', display: 'flex', alignItems: 'center', height: '100%', width: '100%', paddingTop: verticalPad, paddingBottom: verticalPad, marginTop: -verticalPad, marginBottom: -verticalPad }}>
                  {t.buyBack}
                </Link>
              </div>
              {/* fine vertical separator */}
              <div aria-hidden style={{ width: 1, height: 20, background: 'linear-gradient(to bottom, rgba(0,0,0,0), #2b2b2b 30%, #2b2b2b 70%, rgba(0,0,0,0))' }} />
              <div className="nav-item" style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '200%', padding: `0 ${itemPadX}px` }}>
                <Link to="/admin/generate-contract" className="nav-link" style={{ color: tokens.text, textDecoration: 'none', fontWeight: 600, fontSize: 18, position: 'relative', display: 'flex', alignItems: 'center', height: '100%', width: '100%', paddingTop: verticalPad, paddingBottom: verticalPad, marginTop: -verticalPad, marginBottom: -verticalPad }}>
                  {t.generateContract}
                </Link>
              </div>
              {/* fine vertical separator */}
              <div aria-hidden style={{ width: 1, height: 20, background: 'linear-gradient(to bottom, rgba(0,0,0,0), #2b2b2b 30%, #2b2b2b 70%, rgba(0,0,0,0))' }} />
              <div className="nav-item" style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '200%', padding: `0 ${itemPadX}px` }}>
                <Link to="/settings" className="nav-link" style={{ color: tokens.text, textDecoration: 'none', fontWeight: 600, fontSize: 18, position: 'relative', display: 'flex', alignItems: 'center', height: '100%', width: '100%', paddingTop: verticalPad, paddingBottom: verticalPad, marginTop: -verticalPad, marginBottom: -verticalPad }}>
                  {t.siteSettings}
                </Link>
              </div>
            </>
          )}
          {/* No admin link in left menu when not logged in */}
        </div>
      </div>

      {/* CENTER LOGO */}
      <div style={{
        flex: isMobile ? undefined : 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'flex-start',
        minWidth: isMobile ? undefined : 120,
        width: 'auto',
        gridColumn: isMobile ? '2' : undefined,
        position: isMobile ? 'absolute' : undefined,
        left: isMobile ? '50%' : undefined,
        transform: isMobile ? 'translateX(calc(-50% - 12px))' : undefined,
        zIndex: 1,
      }}>
        <CenterLogo />
      </div>

      {/* RIGHT SIDE (ADMIN LOGIN + LANGUAGE) - desktop only, scaled down 10% */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 16,
        gridColumn: isMobile ? '3' : undefined,
        transform: isMobile ? undefined : `scale(${desktopScale})`,
        transformOrigin: 'right center'
      }}>
        {/* Admin login icon and text, always visible on desktop, before language selector */}
        {/* Desktop-only: show login if not authenticated, logout if authenticated, both subtle */}
        {!isMobile && !isAuth && (
          <Link to="/login" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, textDecoration: 'none', padding: '2px 8px', borderRadius: 6, background: 'none', border: 'none', fontWeight: 500, color: tokens.textMuted, fontSize: 15, opacity: 0.92, transition: 'color 0.2s', minWidth: 70 }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ marginBottom: 0 }}>
              <circle cx="12" cy="12" r="11" fill={tokens.textMuted} />
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill={tokens.surface} />
            </svg>
            <span style={{ letterSpacing: 0.2, textAlign: 'center', fontSize: 15, marginTop: 2 }}>{t.login}</span>
          </Link>
        )}
        {!isMobile && isAuth && (
          <button onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none', padding: '2px 8px', borderRadius: 6, background: 'none', border: 'none', fontWeight: 500, color: tokens.accent, fontSize: 15, opacity: 0.85, cursor: 'pointer', transition: 'color 0.2s' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill={tokens.accent} />
              <path d="M10 7h2v4h4v2h-4v4h-2V7zm-2 2v6H6V9h2z" fill={tokens.inverse} />
            </svg>
            <span style={{ letterSpacing: 0.2 }}>{t.logout}</span>
          </button>
        )}
        <LanguageSwitcher />
      </div>
    </nav>
  );
}

function App() {
  const [language, setLanguage] = useState('ro');
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('token') || '');
  const isAuth = !!authToken;
  const { tokens } = useTheme?.() || { tokens: { bg: '#121212', text: '#fff' } }; // fallback if context not yet ready

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      <div style={{ background: tokens.bg, minHeight: '100vh', color: tokens.text, fontFamily: 'Segoe UI, Arial, sans-serif', overflow: 'hidden', maxWidth: '100vw' }}>
        <Router>
          <AuthContext.Provider value={{ token: authToken, setToken: (tok) => { setAuthToken(tok || ''); try { if (tok) localStorage.setItem('token', tok); } catch {} } }}>
            <Navbar />
            <DemoGate>
              <Routes>
                <Route path="/" element={<Listings />} />
                <Route path="/car/:id" element={<CarDetails />} />
                <Route path="/buyback/:carId" element={<BuyBack />} />
                <Route path="/evaluare" element={<BuyBack />} />
                <Route path="/login" element={<Login />} />
                {isAuth && <Route path="/dashboard" element={<Dashboard />} />}
                {isAuth && <Route path="/add" element={<AddCar />} />}
                {isAuth && <Route path="/edit/:id" element={<EditCar />} />}
                {isAuth && <Route path="/admin/buyback" element={<BuyBackAdmin />} />}
                {isAuth && <Route path="/admin/generate-contract" element={<GenerateContract />} />}
                {isAuth && <Route path="/settings" element={<SiteSettings />} />}
              </Routes>
              {/* Global mobile contact buttons & footer */}
              <MobileContactButtons />
              <SiteFooter />
            </DemoGate>
          </AuthContext.Provider>
        </Router>
      </div>
    </LanguageContext.Provider>
  );
}

export { useLanguage, useAuth, translations };

// Wrap App with ThemeProvider at export site so index.js can keep importing default
function AppWithProviders() {
  return (
    <ThemeProvider>
      <App />
    </ThemeProvider>
  );
}
export default AppWithProviders;
